import type { LoanEvent, LoanEventType, RepaymentConsequence } from '@/types';
import { formatInr, formatDate } from '@/lib/format';

export const EVENT_TYPE_ICONS: Record<LoanEventType, string> = {
  disbursement: '💰',
  rate_change: '📈',
  prepayment: '⬇️',
  balance_correction: '✅',
  charge: '🧾',
  emi_date_change: '📅',
};

/**
 * Renders the shared "what happens to EMI/tenure" consequence as a short
 * trailing phrase, e.g. " — EMI → ₹24,905 (Projected)". Returns an empty
 * string if no consequence was recorded, so callers can append it
 * unconditionally.
 */
function consequenceSuffix(consequence: RepaymentConsequence | undefined): string {
  if (!consequence) return '';
  const parts: string[] = [];
  if (consequence.newEmi !== undefined) parts.push(`EMI → ${formatInr(consequence.newEmi, true)}`);
  if (consequence.newRemainingTenureMonths !== undefined) {
    parts.push(`Tenure → ${consequence.newRemainingTenureMonths} months`);
  }
  const statusLabel = consequence.status === 'actual_lic' ? 'LIC-Confirmed' : 'Projected';
  if (parts.length === 0) return ` — (${statusLabel}, details pending)`;
  return ` — ${parts.join(', ')} (${statusLabel})`;
}

/**
 * Produces a one-line, human-readable summary for a timeline item, e.g.
 * "₹8,50,000 disbursed" or "Rate changed to 8.00% p.a.". Kept centralised
 * here so the Timeline list and any future detail view render identical
 * summaries.
 */
export function summarizeEvent(event: LoanEvent): string {
  switch (event.type) {
    case 'disbursement':
      return `${formatInr(event.amount)} disbursed${event.trancheLabel ? ` — ${event.trancheLabel}` : ''}`;
    case 'rate_change':
      return `Rate changed to ${event.annualRatePercent.toFixed(2)}% p.a.${consequenceSuffix(event.repaymentConsequence)}`;
    case 'prepayment': {
      const kind = event.paymentType === 'principal_prepayment' ? 'Principal prepayment' : 'Advance EMI payment';
      let intentSuffix = '';
      if (event.repaymentConsequence) {
        const intentLabel =
          event.repaymentConsequence.intent === 'reduce_tenure'
            ? 'reduce tenure'
            : event.repaymentConsequence.intent === 'reduce_emi'
              ? 'reduce EMI'
              : event.repaymentConsequence.intent === 'both'
                ? 'EMI & tenure change'
                : 'pending LIC action';
        intentSuffix = ` (${intentLabel})`;
      }
      return `${kind} of ${formatInr(event.amount)}${intentSuffix}${consequenceSuffix(event.repaymentConsequence)}`;
    }
    case 'balance_correction':
      return `LIC-confirmed balance: ${formatInr(event.licConfirmedBalance, true)}${consequenceSuffix(event.repaymentConsequence)}`;
    case 'charge': {
      const categoryLabel =
        event.category === 'insurance' ? 'Insurance' : event.category === 'processing_fee' ? 'Processing fee' : 'Other charge';
      return `${categoryLabel}: ${formatInr(event.amount, true)}${event.financedIntoPrincipal ? ' (financed into principal)' : ''}`;
    }
    case 'emi_date_change':
      return `EMI date changed to the ${ordinal(event.newEmiDayOfMonth)} of each month`;
  }
}

function ordinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

export function eventDateLabel(event: LoanEvent): string {
  return `Effective ${formatDate(event.effectiveDate)}`;
}
